import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import useApi from "../../../hooks/useApi";

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 40;

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-sizing: border-box;
    overflow: hidden;
`;

const HeaderBar = styled.div`
    width: 100%;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
    flex-shrink: 0;
`;

const Badge = styled.div`
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.9);
    padding: 8px 10px;
    border-radius: 10px;
    font-size: 13px;
    white-space: nowrap;
`;

const FilterInput = styled.input`
    height: 40px;
    flex: 1;
    min-width: 240px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    padding: 0 12px;
    box-sizing: border-box;
    outline: none;

    &::placeholder {
        color: rgba(255, 255, 255, 0.7);
    }

    &:focus {
        border-color: rgba(0, 123, 255, 0.9);
        box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }
`;

const TableShell = styled.div`
    width: 100%;
    flex: 1;
    min-height: 0;
    background-color: #ffffff;
    border-radius: 15px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

function buildSearchHaystack(row) {
    return [
        row.cpf,
        row.matricula,
        row.nome,
        row.funcao,
        row.lider,
        row.centro_custo,
        row.contrato,
        row.mao_obra,
        row.status,
    ]
        .filter((v) => v != null && String(v).trim() !== "")
        .join(" ")
        .toUpperCase();
}

const Content = () => {
    const api = useApi();
    const tableRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [colaboradores, setColaboradores] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [q, setQ] = useState("");

    useEffect(() => {
        const fetchColaboradores = async () => {
            setLoading(true);

            try {
                const { data } = await api.get("/Painel/API/Almoxarifado/Colaboradores/index.php");
                setColaboradores(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error(error);
                setColaboradores([]);
            } finally {
                setLoading(false);
            }
        };

        fetchColaboradores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const query = q.trim().toUpperCase();
        if (!query) return colaboradores;

        return colaboradores.filter((row) => buildSearchHaystack(row).includes(query));
    }, [colaboradores, q]);

    const totalAtivos = useMemo(
        () => filtered.filter((row) => row.status === "ATIVO").length,
        [filtered]
    );

    const totalFerias = useMemo(
        () => filtered.filter((row) => row.status === "FÉRIAS").length,
        [filtered]
    );

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
        if (tableRef.current) {
            tableRef.current.scrollTop = 0;
        }
    }, [filtered]);

    const loadMoreRows = useCallback(() => {
        setVisibleCount((prev) => {
            const total = filtered.length;
            if (prev >= total) return prev;
            return Math.min(prev + PAGE_SIZE, total);
        });
    }, [filtered.length]);

    const columns = useMemo(
        () => [
            {
                id: "matricula",
                header: "MATRÍCULA",
                accessor: "matricula",
                sortable: true,
                width: "110px",
            },
            {
                id: "nome",
                header: "NOME",
                accessor: "nome",
                sortable: true,
            },
            {
                id: "funcao",
                header: "FUNÇÃO",
                accessor: "funcao",
                sortable: true,
            },
            {
                id: "lider",
                header: "LÍDER",
                accessor: "lider",
                sortable: true,
                render: (value) => value || "-",
            },
            {
                id: "centro_custo",
                header: "CENTRO DE CUSTO",
                accessor: "centro_custo",
                sortable: true,
            },
            {
                id: "contrato",
                header: "CONTRATO",
                accessor: "contrato",
                sortable: true,
                render: (value) => value || "-",
            },
            {
                id: "mao_obra",
                header: "MÃO DE OBRA",
                accessor: "mao_obra",
                sortable: true,
                width: "130px",
                render: (value) => value || "-",
            },
            {
                id: "status",
                header: "STATUS",
                accessor: "status",
                sortable: true,
                width: "100px",
            },
        ],
        []
    );

    return (
        <PageContent>
            <HeaderBar>
                <Badge>Ativos: {loading ? "..." : totalAtivos}</Badge>
                <Badge>Férias: {loading ? "..." : totalFerias}</Badge>
                <FilterInput
                    placeholder="Pesquisar por CPF, matrícula, nome, função, líder, centro de custo, contrato..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
            </HeaderBar>

            <TableShell>
                <TableFlexFill>
                    <SortableTable
                        ref={tableRef}
                        columns={columns}
                        data={filtered}
                        getRowId={(row) => row.matricula}
                        isLoading={loading}
                        loadingText="Carregando colaboradores..."
                        emptyText="NENHUM COLABORADOR ENCONTRADO"
                        initialSort={{ key: "nome", direction: "asc" }}
                        limit={visibleCount}
                        onScrollReachEnd={loadMoreRows}
                        scrollReachThreshold={SCROLL_THRESHOLD}
                    />
                </TableFlexFill>
            </TableShell>
        </PageContent>
    );
};

export default Content;
